import React, { useState, useEffect } from 'react';
import { CreditCard, Users, Building, Check, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from '../ui/simple-toaster';
import { api } from '../../utils/api';

const PRICING = {
  individual: {
    monthly: 1500,  // $15.00
    annual: 15000,  // $150.00 (saves $30)
    label: 'Individual Driver',
    description: 'Driver screens + PDF export',
    features: [
      'Driver work diary interface',
      'Real-time compliance tracking',
      'PDF work diary export',
      'Mobile responsive app'
    ]
  },
  business: {
    monthly: 2000,  // $20.00 per driver
    annual: 20000,  // $200.00 per driver (saves $40)
    label: 'Business',
    description: 'Full fleet management',
    features: [
      'Everything in Individual',
      'Admin dashboard',
      'Compliance reports',
      'CSV import/export',
      'Multi-driver management',
      'Flexible driver counts',
      'Priority support'
    ]
  }
};

const SubscriptionPanel = ({ user, driverCount = 0 }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('business');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [driverSeats, setDriverSeats] = useState(Math.max(driverCount, 1));

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const result = await api('/subscription');
      setSubscription(result.subscription);
    } catch (err) {
      console.error('Failed to load subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const result = await api('/stripe/create-checkout', {
        method: 'POST',
        body: JSON.stringify({
          plan_type: selectedPlan,
          billing_cycle: billingCycle,
          driver_count: selectedPlan === 'business' ? driverSeats : 1,
          success_url: `${window.location.origin}/billing?success=true`,
          cancel_url: `${window.location.origin}/billing?canceled=true`
        })
      });

      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      toast.error('Failed to start checkout: ' + err.message);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const result = await api('/stripe/create-portal', { method: 'POST' });
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      toast.error('Failed to open billing portal: ' + err.message);
    }
  };

  const calculatePrice = (plan) => {
    const pricing = PRICING[plan];
    const basePrice = billingCycle === 'monthly' ? pricing.monthly : pricing.annual;
    const multiplier = plan === 'business' ? driverSeats : 1;
    return (basePrice * multiplier) / 100;
  };

  const formatPrice = (cents) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  // Active subscription view
  if (subscription?.status === 'ACTIVE') {
    return (
      <div className="space-y-6">
        <div className="bg-slate-800/50 rounded-2xl border border-emerald-500/30 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Check className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Active Subscription</h2>
              <p className="text-emerald-400 text-sm">Your subscription is active</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-slate-700/50 rounded-xl p-4">
              <p className="text-slate-400 text-sm">Plan</p>
              <p className="text-white font-semibold text-lg capitalize">
                {subscription.plan_type || 'Business'}
              </p>
            </div>
            <div className="bg-slate-700/50 rounded-xl p-4">
              <p className="text-slate-400 text-sm">Driver Seats</p>
              <p className="text-white font-semibold text-lg">
                {subscription.driver_count || driverCount}
              </p>
            </div>
            <div className="bg-slate-700/50 rounded-xl p-4">
              <p className="text-slate-400 text-sm">Next Billing</p>
              <p className="text-white font-semibold text-lg">
                {subscription.current_period_end 
                  ? new Date(subscription.current_period_end).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>
          </div>

          <button
            onClick={handleManageSubscription}
            className="mt-6 px-6 py-3 bg-slate-700 text-white rounded-xl font-semibold hover:bg-slate-600 flex items-center gap-2"
            data-testid="manage-subscription-btn"
          >
            <ExternalLink className="w-5 h-5" />
            Manage Subscription
          </button>
        </div>
      </div>
    );
  }

  // Pricing view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Choose Your Plan</h1>
        <p className="text-slate-400">Simple, transparent pricing for driver fatigue compliance</p>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-6">
        <div className="bg-slate-800 rounded-xl p-1 flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-lg font-medium transition ${
              billingCycle === 'monthly'
                ? 'bg-emerald-500 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="billing-monthly"
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-6 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
              billingCycle === 'annual'
                ? 'bg-emerald-500 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="billing-annual"
          >
            Annual
            <span className="px-2 py-0.5 bg-emerald-400/20 text-emerald-300 text-xs rounded-full">
              Save 17%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Individual Plan */}
        <div
          className={`bg-slate-800/50 rounded-2xl border p-6 transition cursor-pointer ${
            selectedPlan === 'individual'
              ? 'border-emerald-500'
              : 'border-slate-700 hover:border-slate-600'
          }`}
          onClick={() => setSelectedPlan('individual')}
          data-testid="plan-individual"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{PRICING.individual.label}</h3>
              <p className="text-slate-400 text-sm">{PRICING.individual.description}</p>
            </div>
          </div>

          <div className="mb-6">
            <span className="text-4xl font-bold text-white">
              {formatPrice(billingCycle === 'monthly' ? PRICING.individual.monthly : PRICING.individual.annual)}
            </span>
            <span className="text-slate-400">
              /{billingCycle === 'monthly' ? 'month' : 'year'}
            </span>
          </div>

          <ul className="space-y-3">
            {PRICING.individual.features.map((feature) => (
              <li key={feature} className="flex items-center gap-2 text-slate-300">
                <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Business Plan */}
        <div
          className={`bg-slate-800/50 rounded-2xl border p-6 transition cursor-pointer relative ${
            selectedPlan === 'business'
              ? 'border-emerald-500'
              : 'border-slate-700 hover:border-slate-600'
          }`}
          onClick={() => setSelectedPlan('business')}
          data-testid="plan-business"
        >
          <div className="absolute -top-3 right-4 px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
            RECOMMENDED
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Building className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{PRICING.business.label}</h3>
              <p className="text-slate-400 text-sm">{PRICING.business.description}</p>
            </div>
          </div>

          <div className="mb-4">
            <span className="text-4xl font-bold text-white">
              {formatPrice(billingCycle === 'monthly' ? PRICING.business.monthly : PRICING.business.annual)}
            </span>
            <span className="text-slate-400">
              /driver/{billingCycle === 'monthly' ? 'month' : 'year'}
            </span>
          </div>

          {/* Driver Seats Selector */}
          <div className="mb-6 p-4 bg-slate-700/50 rounded-xl">
            <label className="block text-sm text-slate-400 mb-2">Number of Drivers</label>
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => { e.stopPropagation(); setDriverSeats(Math.max(1, driverSeats - 1)); }}
                className="w-10 h-10 bg-slate-600 rounded-lg text-white hover:bg-slate-500 text-xl font-bold"
              >
                -
              </button>
              <input
                type="number"
                value={driverSeats}
                onChange={(e) => setDriverSeats(Math.max(1, parseInt(e.target.value) || 1))}
                onClick={(e) => e.stopPropagation()}
                className="w-20 text-center bg-slate-600 border-none rounded-lg text-white text-lg font-semibold py-2"
                min="1"
                data-testid="driver-seats-input"
              />
              <button
                onClick={(e) => { e.stopPropagation(); setDriverSeats(driverSeats + 1); }}
                className="w-10 h-10 bg-slate-600 rounded-lg text-white hover:bg-slate-500 text-xl font-bold"
              >
                +
              </button>
              <span className="text-slate-400 ml-2">
                = {formatPrice((billingCycle === 'monthly' ? PRICING.business.monthly : PRICING.business.annual) * driverSeats)}/{billingCycle === 'monthly' ? 'mo' : 'yr'}
              </span>
            </div>
            {driverCount > 0 && driverSeats < driverCount && (
              <div className="flex items-center gap-2 mt-2 text-amber-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                You have {driverCount} drivers. Consider increasing seats.
              </div>
            )}
          </div>

          <ul className="space-y-3">
            {PRICING.business.features.map((feature) => (
              <li key={feature} className="flex items-center gap-2 text-slate-300">
                <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Checkout Button */}
      <div className="max-w-md mx-auto mt-8">
        <button
          onClick={handleCheckout}
          disabled={checkoutLoading}
          className="w-full px-6 py-4 bg-emerald-500 text-white rounded-xl font-semibold text-lg hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="checkout-btn"
        >
          {checkoutLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Preparing checkout...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Subscribe - {formatPrice(calculatePrice(selectedPlan) * 100)}/{billingCycle === 'monthly' ? 'month' : 'year'}
            </>
          )}
        </button>
        <p className="text-center text-slate-500 text-sm mt-3">
          Secure payment powered by Stripe. Cancel anytime.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionPanel;
